import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Dispatch } from 'react';

import { useEmailApiClient } from '@/api/clients/useEmailApiClient.util.ts';
import { EmailThreadDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { EMAILS_REDUCER_EVENT, useEmailsContext } from '@/views/emails/context/EmailsContext.ts';
import { EmailsReducerAction } from '@/views/emails/context/EmailsContextProvider.tsx';
import { scheduleThreadListRefresh } from '@/views/emails/util/threadListCache.util.ts';
import { useCurrentThreadsQueryKey } from '@/views/emails/util/useCurrentThreadsQueryKey.util.ts';
import { UseReadEmailsResponse } from '@/views/emails/util/useReadEmails.util.tsx';

type Req = {
  emailId: string;
  threadId: string;
};

type Return = {
  deleteEmail: (req: Req) => Promise<void>;
  isDeleting: boolean;
};

export const useDeleteEmail = (): Return => {
  const { emailUpdateApi } = useEmailApiClient();
  const { userId } = useUserContext();
  const queryClient = useQueryClient();
  const { updateEmailState } = useEmailsContext();
  const currentThreadsKey = useCurrentThreadsQueryKey();

  const { isPending, mutateAsync } = useMutation<void, AxiosError, Req>({
    meta: { disableSuccessAlert: true },
    mutationFn: async req => {
      await emailUpdateApi.deleteEmail(userId, req.emailId);
    },
    mutationKey: [QueryKeyEnum.EMAILS.DELETE],
    onMutate: async req => {
      await queryClient.cancelQueries({ queryKey: currentThreadsKey });
      queryClient.setQueryData<EmailThreadDto>(
        [QueryKeyEnum.EMAILS.THREAD, req.threadId],
        cur => cur && { ...cur, emails: cur.emails.filter(email => email.emailId !== req.emailId) },
      );
      queryClient.setQueryData<InfiniteData<UseReadEmailsResponse | undefined>>(
        currentThreadsKey,
        cur => removeEmailFromInfiniteData(cur, req, updateEmailState),
      );
    },
    onSettled: async (_data, _error, req) => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeyEnum.EMAILS.THREAD, req.threadId] });
      scheduleThreadListRefresh(queryClient);
    },
  });

  return {
    deleteEmail: mutateAsync,
    isDeleting: isPending,
  };
};

const removeEmailFromInfiniteData = (
  cur: InfiniteData<UseReadEmailsResponse | undefined> | undefined,
  req: Req,
  updateEmailState: Dispatch<EmailsReducerAction>,
): InfiniteData<UseReadEmailsResponse | undefined> | undefined => {
  if (!cur) return cur;

  const newPages = [...cur.pages];

  for (let pageIndex = 0; pageIndex < newPages.length; pageIndex++) {
    const page = newPages[pageIndex];
    if (!page) continue;

    for (let threadIndex = 0; threadIndex < page.items.length; threadIndex++) {
      const thread = page.items[threadIndex];
      if (thread.threadId !== req.threadId) continue;

      const emailIndex = thread.emails.findIndex(email => email.emailId === req.emailId);
      if (emailIndex === -1) continue;

      const newEmails = thread.emails.slice();
      newEmails.splice(emailIndex, 1);

      const newItems = page.items.slice();
      if (newEmails.length > 0) {
        newItems[threadIndex] = { ...thread, emails: newEmails };
      } else {
        newItems.splice(threadIndex, 1);
        updateEmailState({
          payload: null,
          type: EMAILS_REDUCER_EVENT.SELECT_THREAD,
        });
      }

      newPages[pageIndex] = { ...page, items: newItems };

      return { ...cur, pages: newPages };
    }
  }

  return cur;
};
